import { FulfillmentStatus, type OrderLineItemDto } from "@lib/ShiOneClient";
import type { StackedDrawerProps } from "shared-ui/src/components/detail-drawer/drawer-stack";
import { resolveCarrierTrackUrl } from "../order-detail-panel/constants";
import {
  groupItemsByShipment,
  type ShipmentGroup,
} from "../order-detail-panel/ShipmentMeta";
import type { OrderRow } from "@features/orders-features/types/types";
import type { StackedTrackingDrawerProps } from "./StackedTrackingDrawer";
import {
  getShipmentRouteKey,
  toOrderRouteKey,
  toScopedRouteId,
} from "./orderRouteKey";

export type TrackingDrawerPushProps = Omit<
  StackedTrackingDrawerProps,
  keyof StackedDrawerProps
>;

type TrackingOrder = Pick<
  OrderRow,
  "salesOrder" | "purchaseOrder" | "biOrderId" | "webOrderId" | "shipTo"
>;

/** Builds the tracking drawer props for one shipment of an order. */
export function buildTrackingDrawerProps(
  order: Omit<TrackingOrder, "salesOrder"> & { salesOrder?: string },
  group: ShipmentGroup,
  status: string,
): TrackingDrawerPushProps | undefined {
  if (!group.trackingNumber) {
    return undefined;
  }
  const firstItemWithDelivery = group.items.find((item) => item.deliveryPerson);
  return {
    trackingNumber: group.trackingNumber,
    carrier: group.carrier ?? "Other",
    trackingUrl: resolveCarrierTrackUrl(
      group.carrierEnum,
      group.trackingNumber,
    ),
    shippedOn: group.shippedOn,
    deliveryOn: group.deliveryOn,
    status,
    salesOrder: order.salesOrder,
    purchaseOrder: order.purchaseOrder,
    biOrderId: order.biOrderId,
    receivedBy: firstItemWithDelivery?.deliveryPerson ?? null,
    shipToName: order.shipTo?.contact ?? null,
    shipToCompany: order.shipTo?.recipient ?? null,
    shipToAddress: order.shipTo?.address ?? [],
  };
}

/**
 * Builds the URL id of the tracking drawer, `{orderKey}~{shipmentKey}` such as
 * `bi-81234~1a2b3c4d`. The shipment key is a hash, so the tracking number stays out of the URL.
 */
export function toTrackingRouteId(
  order: TrackingOrder,
  trackingNumber: string | null | undefined,
): string | undefined {
  return toScopedRouteId(
    toOrderRouteKey({
      salesOrder: order.salesOrder,
      biOrderId: order.biOrderId,
      webOrderId: order.webOrderId,
    }),
    getShipmentRouteKey(trackingNumber),
  );
}

const SHIPPING_STATUSES: readonly string[] = [
  FulfillmentStatus.Shipped,
  FulfillmentStatus.Delivered,
];

/**
 * Finds the shipment whose tracking number hashes to `shipmentKey` among the shipped and
 * delivered lines of `order`, and builds its tracking drawer props. Used when a tracking link
 * is opened without the props of an in-app open.
 */
export function findTrackingDrawerProps(
  order: OrderRow,
  shipmentKey: string,
): TrackingDrawerPushProps | undefined {
  const sections = Object.entries(
    (order.orderLineProducts ?? {}) as Record<string, OrderLineItemDto[]>,
  );
  for (const [status, items] of sections) {
    if (!SHIPPING_STATUSES.includes(status) || !Array.isArray(items)) {
      continue;
    }
    const group = groupItemsByShipment(items).find(
      (candidate) =>
        getShipmentRouteKey(candidate.trackingNumber) === shipmentKey,
    );
    if (group) {
      return buildTrackingDrawerProps(order, group, status);
    }
  }
  return undefined;
}
